import { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import toast from 'react-hot-toast'
import {
  ClockIcon,
  CalendarDaysIcon,
  BanknotesIcon,
  CubeTransparentIcon,
  CheckIcon,
} from '@heroicons/react/24/outline'
import { GET_DISCOUNT_RULE } from '../../graphql/discount-queries'
import { GET_PRODUCTS } from '../../graphql/product-queries'
import { UPDATE_DISCOUNT_RULE } from '../../graphql/discount-mutations'
import LoadingSpinner from '../../components/LoadingSpinner'
import type { DiscountRule, DiscountCondition, condition_type } from '../../types/discount'

// =============================================================================
// Helpers
// =============================================================================

function safeJsonParse(value: string): unknown {
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

// Local on purpose, not lib/format.ts: DiscountRule / DiscountCode timestamps are still
// epoch milliseconds on the API (its todo, audit 2026-10-08) — the Order fields went ISO.
function parseApiDate(val: string | undefined | null): Date | null {
  if (!val) return null
  const num = Number(val)
  const date = isNaN(num) ? new Date(val) : new Date(num)
  return isNaN(date.getTime()) ? null : date
}

function toLocalDateTime(isoStr: string | undefined | null): string {
  const date = parseApiDate(isoStr)
  if (!date) return ''
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
}

// =============================================================================
// Conditions state
// =============================================================================

interface ConditionValues {
  time_from: string
  time_to: string
  days: number[]
  min_order_value: string
  required_items: number[]
}

interface ConditionsEnabled {
  time_window: boolean
  days_of_week: boolean
  min_order_value: boolean
  required_items: boolean
}

function initConditions(conditions: DiscountCondition[]) {
  const enabled: ConditionsEnabled = {
    time_window: false,
    days_of_week: false,
    min_order_value: false,
    required_items: false,
  }
  const values: ConditionValues = {
    time_from: '',
    time_to: '',
    days: [],
    min_order_value: '',
    required_items: [],
  }

  for (const c of conditions) {
    const v = safeJsonParse(c.value_json)
    switch (c.type) {
      case 'TIME_FROM':
        enabled.time_window = true
        values.time_from = String(v)
        break
      case 'TIME_TO':
        enabled.time_window = true
        values.time_to = String(v)
        break
      case 'DAYS_OF_WEEK':
        enabled.days_of_week = true
        values.days = Array.isArray(v) ? (v as number[]) : []
        break
      case 'MIN_ORDER_VALUE':
        enabled.min_order_value = true
        values.min_order_value = typeof v === 'number' ? String(v / 100) : ''
        break
      case 'REQUIRED_ITEMS':
        enabled.required_items = true
        values.required_items = Array.isArray(v) ? (v as number[]) : []
        break
    }
  }

  return { enabled, values }
}

function buildConditions(
  enabled: ConditionsEnabled,
  values: ConditionValues
): Array<{ type: condition_type; value_json: unknown }> {
  const out: Array<{ type: condition_type; value_json: unknown }> = []

  if (enabled.time_window) {
    if (values.time_from) out.push({ type: 'TIME_FROM', value_json: values.time_from })
    if (values.time_to) out.push({ type: 'TIME_TO', value_json: values.time_to })
  }
  if (enabled.days_of_week && values.days.length > 0) {
    out.push({ type: 'DAYS_OF_WEEK', value_json: values.days })
  }
  if (enabled.min_order_value && values.min_order_value) {
    out.push({
      type: 'MIN_ORDER_VALUE',
      value_json: Math.round(parseFloat(values.min_order_value) * 100),
    })
  }
  if (enabled.required_items && values.required_items.length > 0) {
    out.push({ type: 'REQUIRED_ITEMS', value_json: values.required_items })
  }

  return out
}

// =============================================================================
// VariantPicker (for required items condition)
// =============================================================================

const DAYS_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

function VariantPicker({
  selectedIds,
  onChange,
}: {
  selectedIds: number[]
  onChange: (ids: number[]) => void
}) {
  const [search, setSearch] = useState('')
  const { data, loading } = useQuery(GET_PRODUCTS)
  const products: {
    id: number
    name: string
    variants: { id: number; name: string; price_cents: number; active: boolean }[]
  }[] = data?.products ?? []

  const filtered = products
    .map((p) => ({
      ...p,
      variants: p.variants.filter(
        (v) =>
          v.active &&
          (p.name.toLowerCase().includes(search.toLowerCase()) ||
            v.name.toLowerCase().includes(search.toLowerCase()))
      ),
    }))
    .filter((p) => p.variants.length > 0)

  function toggle(id: number) {
    onChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id])
  }

  function toggleAll(variantIds: number[]) {
    const allSelected = variantIds.every((id) => selectedIds.includes(id))
    if (allSelected) {
      onChange(selectedIds.filter((id) => !variantIds.includes(id)))
    } else {
      const toAdd = variantIds.filter((id) => !selectedIds.includes(id))
      onChange([...selectedIds, ...toAdd])
    }
  }

  return (
    <div className="space-y-2">
      <input
        className="input w-full text-sm"
        placeholder="Search products or variants…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {loading && <p className="text-sm text-gray-400">Loading…</p>}
      <div className="max-h-52 overflow-y-auto space-y-2 pr-1">
        {filtered.map((product) => {
          const variantIds = product.variants.map((v) => v.id)
          const allSelected = variantIds.every((id) => selectedIds.includes(id))
          const someSelected = variantIds.some((id) => selectedIds.includes(id))
          return (
            <div key={product.id} className="border border-gray-100 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => toggleAll(variantIds)}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm font-semibold transition-colors ${
                  allSelected
                    ? 'bg-indigo-50 text-indigo-900'
                    : someSelected
                      ? 'bg-indigo-50/50 text-gray-900'
                      : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span
                  className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                    allSelected
                      ? 'bg-indigo-600 border-indigo-600'
                      : someSelected
                        ? 'bg-indigo-200 border-indigo-400'
                        : 'border-gray-300'
                  }`}
                >
                  {(allSelected || someSelected) && <CheckIcon className="h-3 w-3 text-white" />}
                </span>
                {product.name}
              </button>
              <div className="divide-y divide-gray-50">
                {product.variants.map((v) => {
                  const checked = selectedIds.includes(v.id)
                  return (
                    <label
                      key={v.id}
                      className={`flex items-center gap-3 pl-8 pr-3 py-2 cursor-pointer transition-colors ${
                        checked ? 'bg-indigo-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(v.id)}
                        className="rounded"
                      />
                      <span className={`text-sm flex-1 ${checked ? 'text-indigo-900 font-medium' : 'text-gray-700'}`}>
                        {v.name}
                      </span>
                      <span className="text-xs text-gray-400">€{(v.price_cents / 100).toFixed(2)}</span>
                    </label>
                  )
                })}
              </div>
            </div>
          )
        })}
        {!loading && filtered.length === 0 && (
          <p className="text-sm text-gray-400 px-3 py-2">No products found.</p>
        )}
      </div>
      {selectedIds.length > 0 && (
        <p className="text-xs text-indigo-600 font-medium">
          {selectedIds.length} variant{selectedIds.length === 1 ? '' : 's'} selected
        </p>
      )}
    </div>
  )
}

// =============================================================================
// Main page
// =============================================================================

export default function EditDiscountRulePage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const ruleId = parseInt(id!, 10)

  const { data, loading } = useQuery(GET_DISCOUNT_RULE, {
    variables: { id: ruleId },
  })

  const rule: DiscountRule | undefined = data?.discountRule

  // --- Basic fields ---
  const [name, setName] = useState('')
  const [effectValue, setEffectValue] = useState('')
  const [multiBuyQty, setMultiBuyQty] = useState('2')
  const [priority, setPriority] = useState('0')
  const [minStaffRole, setMinStaffRole] = useState('')
  const [active, setActive] = useState(true)

  // --- Conditions ---
  const [conditionsEnabled, setConditionsEnabled] = useState<ConditionsEnabled>({
    time_window: false,
    days_of_week: false,
    min_order_value: false,
    required_items: false,
  })
  const [conditionValues, setConditionValues] = useState<ConditionValues>({
    time_from: '',
    time_to: '',
    days: [],
    min_order_value: '',
    required_items: [],
  })

  // --- Limits ---
  const [maxUsesTotal, setMaxUsesTotal] = useState('')
  const [maxPerOrder, setMaxPerOrder] = useState('')
  const [startsAt, setStartsAt] = useState('')
  const [endsAt, setEndsAt] = useState('')

  useEffect(() => {
    if (!rule) return

    setName(rule.name)
    setEffectValue(String(rule.effect_value / 100))
    setMultiBuyQty(rule.multi_buy_qty != null ? String(rule.multi_buy_qty) : '2')
    setPriority(String(rule.priority))
    setMinStaffRole(rule.min_staff_role ?? '')
    setActive(rule.active)

    const { enabled, values } = initConditions(rule.conditions)
    setConditionsEnabled(enabled)
    setConditionValues(values)

    setMaxUsesTotal(rule.limit?.max_uses_total != null ? String(rule.limit.max_uses_total) : '')
    setMaxPerOrder(rule.limit?.max_per_order != null ? String(rule.limit.max_per_order) : '')
    setStartsAt(toLocalDateTime(rule.limit?.starts_at))
    setEndsAt(toLocalDateTime(rule.limit?.ends_at))
  }, [rule])

  const [updateRule, { loading: saving }] = useMutation(UPDATE_DISCOUNT_RULE, {
    refetchQueries: [{ query: GET_DISCOUNT_RULE, variables: { id: ruleId } }],
    onCompleted() {
      toast.success('Discount rule updated')
      navigate(`/discounts/${id}`)
    },
    onError(err) {
      toast.error(err.message)
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) return toast.error('Name is required')

    const parsedEffectValue = Math.round(parseFloat(effectValue) * 100)
    if (isNaN(parsedEffectValue) || parsedEffectValue <= 0) {
      return toast.error('Effect value must be a positive number')
    }

    // For MULTI_BUY rules the group size is editable; mirror the API's qty rule.
    let parsedMultiBuyQty: number | null = null
    if (rule?.effect_type === 'MULTI_BUY') {
      parsedMultiBuyQty = parseInt(multiBuyQty, 10)
      if (!Number.isInteger(parsedMultiBuyQty) || parsedMultiBuyQty < 2) {
        return toast.error('Group size must be a whole number of at least 2')
      }
    }

    const input: Record<string, unknown> = {
      name: name.trim(),
      effect_value: parsedEffectValue,
      priority: parseInt(priority, 10) || 0,
      active,
    }

    // Only send multi_buy_qty for MULTI_BUY rules — the API rejects it elsewhere.
    if (parsedMultiBuyQty != null) {
      input.multi_buy_qty = parsedMultiBuyQty
    }

    if (rule?.trigger_type === 'STAFF_APPLIED' && minStaffRole.trim()) {
      input.min_staff_role = minStaffRole.trim()
    }

    // Conditions — always send the full set (full replacement)
    input.conditions = buildConditions(conditionsEnabled, conditionValues)

    // Limits — only include fields that have values
    const limitObj: Record<string, unknown> = {}
    if (maxUsesTotal) limitObj.max_uses_total = parseInt(maxUsesTotal, 10)
    if (maxPerOrder) limitObj.max_per_order = parseInt(maxPerOrder, 10)
    if (startsAt) limitObj.starts_at = new Date(startsAt).toISOString()
    if (endsAt) limitObj.ends_at = new Date(endsAt).toISOString()
    input.limit = limitObj

    updateRule({ variables: { id: ruleId, input } })
  }

  function toggleCondition(key: keyof ConditionsEnabled) {
    setConditionsEnabled((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  function patchCondition(patch: Partial<ConditionValues>) {
    setConditionValues((prev) => ({ ...prev, ...patch }))
  }

  function toggleDay(day: number) {
    patchCondition({
      days: conditionValues.days.includes(day)
        ? conditionValues.days.filter((d) => d !== day)
        : [...conditionValues.days, day],
    })
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner />
      </div>
    )
  }

  if (!rule) {
    return <div className="card p-8 text-center text-red-600">Rule not found.</div>
  }

  const conditionPills: { key: keyof ConditionsEnabled; label: string; Icon: React.ElementType }[] = [
    { key: 'time_window', label: 'Time window', Icon: ClockIcon },
    { key: 'days_of_week', label: 'Days of week', Icon: CalendarDaysIcon },
    { key: 'min_order_value', label: 'Min. order value', Icon: BanknotesIcon },
    { key: 'required_items', label: 'Required items', Icon: CubeTransparentIcon },
  ]

  return (
    <div className="max-w-lg mx-auto space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <Link to="/discounts" className="hover:text-gray-700">Discounts</Link>
        <span>/</span>
        <Link to={`/discounts/${id}`} className="hover:text-gray-700">{rule.name}</Link>
        <span>/</span>
        <span className="text-gray-900">Edit</span>
      </div>

      <h1 className="text-2xl font-bold text-gray-900">Edit Discount Rule</h1>

      <form onSubmit={handleSubmit} className="space-y-6">

        {/* ── Basic fields ── */}
        <div className="card p-6 space-y-5">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Basics</h2>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input className="input w-full" value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {rule.effect_type === 'PERCENTAGE'
                ? 'Percentage (e.g. 10 for 10%)'
                : rule.effect_type === 'MULTI_BUY'
                  ? 'Group price in € (e.g. 12.50)'
                  : 'Amount in € (e.g. 3.00)'}
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              className="input w-full"
              value={effectValue}
              onChange={(e) => setEffectValue(e.target.value)}
            />
          </div>

          {rule.effect_type === 'MULTI_BUY' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Group size (N)</label>
              <input
                type="number"
                min="2"
                step="1"
                className="input w-full"
                value={multiBuyQty}
                onChange={(e) => setMultiBuyQty(e.target.value)}
              />
              <p className="text-xs text-gray-400 mt-1">
                Any {multiBuyQty || 'N'} qualifying items for the group price above.
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
            <input
              type="number"
              className="input w-full"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            />
            <p className="text-xs text-gray-400 mt-1">
              Higher value wins when multiple discounts qualify for the same order.
            </p>
          </div>

          {rule.trigger_type === 'STAFF_APPLIED' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min staff role</label>
              <select
                className="input"
                value={minStaffRole}
                onChange={(e) => setMinStaffRole(e.target.value)}
              >
                <option value="">Any staff member</option>
                <option value="cashier">Cashier</option>
                <option value="staff">Staff</option>
                <option value="manager">Manager</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          )}

          <div className="flex items-center gap-3">
            <input
              id="active-toggle"
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-indigo-600"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
            />
            <label htmlFor="active-toggle" className="text-sm font-medium text-gray-700">Active</label>
          </div>
        </div>

        {/* ── Conditions ── */}
        <div className="card p-6 space-y-4">
          <div>
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Conditions</h2>
            <p className="text-xs text-gray-400 mt-1">
              Toggle conditions on — leave all off to always apply. Saving replaces the full set.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {conditionPills.map(({ key, label, Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => toggleCondition(key)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                  conditionsEnabled[key]
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </button>
            ))}
          </div>

          {conditionsEnabled.time_window && (
            <div className="border border-gray-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <ClockIcon className="h-4 w-4 text-indigo-500" />
                Time window
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">From</label>
                  <input
                    type="time"
                    className="input w-full"
                    value={conditionValues.time_from}
                    onChange={(e) => patchCondition({ time_from: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Until</label>
                  <input
                    type="time"
                    className="input w-full"
                    value={conditionValues.time_to}
                    onChange={(e) => patchCondition({ time_to: e.target.value })}
                  />
                </div>
              </div>
            </div>
          )}

          {conditionsEnabled.days_of_week && (
            <div className="border border-gray-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <CalendarDaysIcon className="h-4 w-4 text-indigo-500" />
                Days of week
              </div>
              <div className="flex flex-wrap gap-2">
                {DAYS_LABELS.map((d, i) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => toggleDay(i + 1)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                      conditionValues.days.includes(i + 1)
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
          )}

          {conditionsEnabled.min_order_value && (
            <div className="border border-gray-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <BanknotesIcon className="h-4 w-4 text-indigo-500" />
                Minimum order value
              </div>
              <div className="relative w-40">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm select-none">€</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="input w-full pl-7"
                  value={conditionValues.min_order_value}
                  onChange={(e) => patchCondition({ min_order_value: e.target.value })}
                  placeholder="20.00"
                />
              </div>
            </div>
          )}

          {conditionsEnabled.required_items && (
            <div className="border border-gray-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <CubeTransparentIcon className="h-4 w-4 text-indigo-500" />
                Required items
              </div>
              <VariantPicker
                selectedIds={conditionValues.required_items}
                onChange={(ids) => patchCondition({ required_items: ids })}
              />
            </div>
          )}
        </div>

        {/* ── Limits ── */}
        <div className="card p-6 space-y-5">
          <div>
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Limits</h2>
            <p className="text-xs text-gray-400 mt-1">Leave blank to keep existing value or have no cap.</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max total uses</label>
              <input
                type="number"
                min="1"
                className="input w-full"
                value={maxUsesTotal}
                onChange={(e) => setMaxUsesTotal(e.target.value)}
                placeholder="Unlimited"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max per order</label>
              <input
                type="number"
                min="1"
                className="input w-full"
                value={maxPerOrder}
                onChange={(e) => setMaxPerOrder(e.target.value)}
                placeholder="Unlimited"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Active from</label>
            <input
              type="datetime-local"
              className="input w-full"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Active until</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {[7, 30, 90].map((days) => {
                const base = startsAt ? new Date(startsAt) : new Date()
                const target = new Date(base)
                target.setDate(target.getDate() + days)
                const value = new Date(target.getTime() - target.getTimezoneOffset() * 60000)
                  .toISOString()
                  .slice(0, 16)
                return (
                  <button
                    key={days}
                    type="button"
                    onClick={() => setEndsAt(value)}
                    className={`px-3 py-1 rounded-lg text-sm border transition-all ${
                      endsAt === value
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    +{days}d
                  </button>
                )
              })}
              {endsAt && (
                <button
                  type="button"
                  onClick={() => setEndsAt('')}
                  className="px-3 py-1 rounded-lg text-sm border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200"
                >
                  Clear
                </button>
              )}
            </div>
            <input
              type="datetime-local"
              className="input w-full"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
            />
          </div>
        </div>

        {/* ── Actions ── */}
        <div className="flex gap-3">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          <Link to={`/discounts/${id}`} className="btn-secondary">Cancel</Link>
        </div>
      </form>
    </div>
  )
}
